import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { CustomEmoji } from 'types/interfaces/message';
import './EmojiPicker.css';

const COMMON_EMOJIS = [
  '😀', '😃', '😄', '😁', '😊', '😉', '😍', '🥰', '😘', '😗',
  '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶',
  '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴',
  '😆', '😂', '😅', '😭', '🤤', '🤮', '🫡', '🫂', '🤦‍♂️', '👀',
  '👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '🤲', '🙏',
  '🤘', '☝️', '👋', '🤌', '💅', '💪', '✨', '🎉', '🎊', '🎈',
  '🎁', '🏆', '🥇', '🥈', '🥉', '💯', '🔥', '✅', '🚬', '🚨',
  '📦', '💰', '💀', '🚀', '❓', '💎', '🍻', '‼️',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🤍', '🖤', '💔', '❣️',
];

interface Props {
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  showCustomSection?: boolean;
  customEmojis?: CustomEmoji[];
  onAddCustomEmoji?: () => void;
  searchable?: boolean;
  usePortal?: boolean;
}

export const EmojiPicker: React.FC<Props> = ({
  onEmojiSelect,
  onClose,
  buttonRef,
  showCustomSection = false,
  customEmojis,
  onAddCustomEmoji,
  searchable = false,
  usePortal = false,
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'above' | 'below'>('below');
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const pickerHeight = 200;

    const pos = spaceBelow < pickerHeight && spaceAbove > spaceBelow ? 'above' : 'below';
    setPosition(pos);

    if (usePortal) {
      const style: React.CSSProperties = {
        left: buttonRect.left,
      };
      if (pos === 'below') {
        style.top = buttonRect.bottom + 8;
      } else {
        style.bottom = window.innerHeight - buttonRect.top + 8;
      }
      setPortalStyle(style);
    }

    // Click outside to close
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, buttonRef, usePortal]);

  const filteredStandardEmojis = useMemo(() => {
    if (!searchQuery) return COMMON_EMOJIS;
    // For standard emoji, filter is basic — just return all if no meaningful text match
    // Standard emoji don't have searchable names in this implementation
    return COMMON_EMOJIS;
  }, [searchQuery]);

  const filteredCustomEmojis = useMemo(() => {
    if (!customEmojis) return [];
    if (!searchQuery) return customEmojis;
    const query = searchQuery.toLowerCase();
    return customEmojis.filter(
      (e) => e.name.toLowerCase().includes(query) || e.slug.toLowerCase().includes(query)
    );
  }, [customEmojis, searchQuery]);

  const handleSelect = (emoji: string) => {
    onEmojiSelect(emoji);
    onClose();
  };

  const pickerContent = (
    <div
      ref={pickerRef}
      className={`emoji-picker ${position}${usePortal ? ' portal' : ''}`}
      style={usePortal ? portalStyle : undefined}
    >
      {searchable && (
        <input
          type="text"
          className="emoji-picker-search"
          placeholder="Search emoji..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
      )}

      {showCustomSection && filteredCustomEmojis.length > 0 && (
        <>
          <div className="emoji-section-label">Custom</div>
          <div className="emoji-grid emoji-grid--custom">
            {filteredCustomEmojis.map((emoji) => (
              <button
                key={emoji.slug}
                className="emoji-button emoji-button--custom"
                onClick={() => handleSelect(`:${emoji.slug}:`)}
                title={emoji.name}
                type="button"
              >
                <img
                  src={emoji.imageUrl}
                  alt={emoji.name}
                  className="emoji-custom-img"
                  onError={(e) => {
                    const img = e.currentTarget;
                    img.style.display = 'none';
                    const fallback = document.createElement('span');
                    fallback.className = 'emoji-custom-fallback';
                    fallback.textContent = `:${emoji.slug}:`;
                    img.parentElement?.appendChild(fallback);
                  }}
                />
              </button>
            ))}
          </div>
          {onAddCustomEmoji && (
            <button
              className="emoji-add-custom-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAddCustomEmoji();
              }}
              type="button"
            >
              + Add Emoji
            </button>
          )}
          <div className="emoji-section-label">Standard</div>
        </>
      )}

      {showCustomSection && filteredCustomEmojis.length === 0 && onAddCustomEmoji && (
        <>
          <div className="emoji-section-label">Custom</div>
          <div className="emoji-custom-empty">
            <span>No custom emoji yet</span>
            <button
              className="emoji-add-custom-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAddCustomEmoji();
              }}
              type="button"
            >
              + Add Emoji
            </button>
          </div>
          <div className="emoji-section-label">Standard</div>
        </>
      )}

      <div className="emoji-grid">
        {filteredStandardEmojis.map((emoji, index) => (
          <button
            key={index}
            className="emoji-button"
            onClick={() => handleSelect(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  if (usePortal) {
    return createPortal(pickerContent, document.body);
  }

  return pickerContent;
};
